import { heapEvent } from '@equipmentshare/heaper'
import TransactionForm, { SubmittedTransactionItem } from '../../../../reports/components/TransactionForm'
import { TransactionType } from '../../../../../graphql'
import { TransactionItemFormState } from '../../../../reports/components/TransactionForm/components/AddPartsForm'
import { LineItem } from '../../../../../types/demandRequest'
import { getLineItemTarget } from '../../../utils/formatters'

export type CreateTransferFromRequestModalProps = {
	onCancel: () => void,
	onSuccess: () => void,
	fromInventoryId: number,
	toInventoryId: number,
	selectedLineItems: LineItem[],
	requestId: string,
	requestNumber: number,
	onTransferSuccess?: ( fullyFulfilledLineItemIds: string[], transactionId?: number ) => void,
}

export default function CreateTransferFromRequestModal( { onCancel, onSuccess, fromInventoryId, toInventoryId, selectedLineItems, requestId, requestNumber, onTransferSuccess }: CreateTransferFromRequestModalProps ) {
	let submittedItems: SubmittedTransactionItem[] = []
	let transactionId: number | undefined = undefined

	if ( !selectedLineItems || selectedLineItems.length === 0 ) {
		return null
	}

	// Build initial line items with part reference for display when unavailable
	const initialLineItems: TransactionItemFormState[] = selectedLineItems.map( ( lineItem, idx ) => ( {
		idx,
		selectedProductId: parseInt( lineItem.productId, 10 ),
		part: lineItem.product,
		quantity: String( lineItem.quantityRequested ),
		costPerItem: '',
		validation: {
			quantity: { isInvalid: false },
			costPerItem: { isInvalid: false },
			part: { isInvalid: false },
		},
	} ) )

	// Format memo: prefix with request number, then group each line item's notes and target with commas, then join groups with " / "
	const lineItemsMemo = selectedLineItems
		.map( ( item ) => {
			const notes = []
			if ( item.lineNotes?.trim() ) {
				notes.push( item.lineNotes.trim() )
			}
			if ( item.fulfillerNotes?.trim() ) {
				notes.push( item.fulfillerNotes.trim() )
			}
			// Include the "Item is for" target (e.g., "WO-12345", "INV-67890", "ORD-54321", "Stock")
			const target = getLineItemTarget( item )
			notes.push( target )
			// Join this line item's notes and target with commas
			return notes.join( ', ' )
		} )
		.join( ' / ' )
	const memo = `REQ-${requestNumber}: ${lineItemsMemo}`

	const handleCancel = () => {
		heapEvent( 'Inventory - Stock Request - Create Transfer Modal - Click - Cancel', {
			demand_request_id: requestId,
		} )
		onCancel()
	}

	const handleSubmitClick = () => {
		heapEvent( 'Inventory - Stock Request - Create Transfer Modal - Click - Submit', {
			demand_request_id: requestId,
			item_count: selectedLineItems.length,
		} )
	}

	const handleSubmitSuccess = ( items: SubmittedTransactionItem[], txId?: number ) => {
		submittedItems = items
		transactionId = txId
	}

	const handleSuccess = () => {
		heapEvent( 'Inventory - Stock Request - Create Transfer Modal - Success', {
			demand_request_id: requestId,
			item_count: selectedLineItems.length,
		} )

		// Determine which line items were fully fulfilled based on ACTUAL submitted quantities
		// Create a map of productId -> transferred quantity from submitted items
		const transferredQuantities = new Map<number, number>()
		submittedItems.forEach( ( item ) => {
			const existing = transferredQuantities.get( item.partId ) || 0
			transferredQuantities.set( item.partId, existing + item.quantity )
		} )

		// Check which line items were fully fulfilled
		const fullyFulfilledLineItemIds = selectedLineItems
			.filter( ( lineItem ) => {
				const productId = parseInt( lineItem.productId, 10 )
				const transferredQty = transferredQuantities.get( productId ) || 0
				return transferredQty >= lineItem.quantityRequested
			} )
			.map( ( lineItem ) => lineItem.lineItemId )

		// Call the callback to auto-complete fulfilled items and add fulfiller notes
		if ( onTransferSuccess ) {
			onTransferSuccess( fullyFulfilledLineItemIds, transactionId )
		}

		onSuccess()
	}

	return (
		<TransactionForm
			transactionType={TransactionType.StoreToStore}
			defaultFromId={fromInventoryId}
			defaultToId={toInventoryId}
			defaultMemo={memo}
			initialLineItems={initialLineItems}
			onCancel={handleCancel}
			onSuccess={handleSuccess}
			onSubmitClick={handleSubmitClick}
			onSubmitSuccess={handleSubmitSuccess}
		/>
	)
}
